import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { FaStore, FaCheckCircle, FaTimesCircle, FaSave } from "react-icons/fa";
import {
  getUserStores,
  setStoreStatus,
  getStoreStatusByDate,
  selectStoreStatus,
  StoreStatusState,
  resetStoreStatus,
} from "../slices/store-status.slice";

// FIXED: Helper function to format date for API (no timezone shift)
const formatDateForAPI = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`; // yyyy-mm-dd in local timezone
};

const formatDateToUSFormat = (date: Date): string => {
  return date.toLocaleDateString("en-US"); // mm/dd/yyyy display
};

export function SetStoreStatus() {
  const dispatch = useAppDispatch();
  const storeStatusState = useAppSelector(selectStoreStatus);

  const [selectedStore, setSelectedStore] = useState<any>(null);
  const [status, setStatus] = useState<"open" | "closed">("open");
  const [statusDate, setStatusDate] = useState<Date>(new Date());
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // FIXED: All hooks must be called at the top level
  useEffect(() => {
    dispatch(getUserStores());
  }, [dispatch]);

  // ADDED: Auto-select store if only one store is available
  useEffect(() => {
    if (storeStatusState.userStores.length === 1 && !selectedStore) {
      setSelectedStore(storeStatusState.userStores[0]);
      console.log(
        "Auto-selected single store:",
        storeStatusState.userStores[0].store_name
      );
    }
  }, [storeStatusState.userStores, selectedStore]);

  useEffect(() => {
    if (storeStatusState.status === StoreStatusState.success && isSubmitting) {
      setIsSubmitting(false);
      // Reset form after successful submission (but keep store if only one)
      if (storeStatusState.userStores.length > 1) {
        setSelectedStore(null);
      }
      setStatus("open");
      setNotes("");
      // Refresh the status data
      const apiDate = formatDateForAPI(statusDate);
      dispatch(getStoreStatusByDate({ date: apiDate }));
      // Reset status after showing success message
      setTimeout(() => {
        dispatch(resetStoreStatus());
      }, 3000);
    }
  }, [
    storeStatusState.status,
    isSubmitting,
    dispatch,
    statusDate,
    storeStatusState.userStores.length,
  ]);

  // FIXED: Handle permission check after all hooks are called
  if (storeStatusState.permissionError) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-md mx-auto mt-20">
          <Alert severity="error">
            <Typography variant="h6" className="mb-2">
              Access Denied
            </Typography>
            <Typography variant="body2">
              {storeStatusState.permissionError}
            </Typography>
            <Typography variant="caption" className="block mt-2 text-gray-600">
              Only Team Captains, Managers, and Admins can access Store Status.
            </Typography>
          </Alert>

          <div className="mt-4 text-center">
            <Button variant="outlined" onClick={() => window.history.back()}>
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while checking permissions
  if (
    storeStatusState.status === StoreStatusState.inProgress &&
    !storeStatusState.hasPermission &&
    !storeStatusState.permissionError
  ) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <CircularProgress />
        <Typography className="ml-2">Checking permissions...</Typography>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedStore) {
      return;
    }

    setIsSubmitting(true);

    // FIXED: Use timezone-safe date formatting
    const apiDate = formatDateForAPI(statusDate);
    console.log("Selected date (local):", statusDate);
    console.log("Sending date to API (fixed):", apiDate);
    console.log("Display date:", formatDateToUSFormat(statusDate));

    await dispatch(
      setStoreStatus({
        store_id: selectedStore.store_id,
        status,
        status_date: apiDate, // yyyy-mm-dd format in local timezone
        notes: notes.trim() || undefined,
      })
    );
  };

  const isLoading = storeStatusState.status === StoreStatusState.inProgress;
  const hasOnlyOneStore = storeStatusState.userStores.length === 1;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
            <FaStore className="text-white" size={20} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Set Store Status
            </h1>
            <p className="text-gray-600">
              Update daily store open/closed status
            </p>
          </div>
        </div>
      </div>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent className="p-6">
              <Typography variant="h6" className="mb-4 font-semibold">
                Store Status Form
              </Typography>

              {storeStatusState.status === StoreStatusState.fail && (
                <Alert severity="error" className="mb-4">
                  {storeStatusState.message}
                </Alert>
              )}

              {storeStatusState.status === StoreStatusState.success && (
                <Alert severity="success" className="mb-4">
                  {storeStatusState.message}
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Store Selection */}
                <FormControl fullWidth>
                  <Autocomplete
                    options={storeStatusState.userStores}
                    getOptionLabel={(option) =>
                      `${option.store_name} - ${option.address}`
                    }
                    value={selectedStore}
                    onChange={(_, newValue) => setSelectedStore(newValue)}
                    disabled={hasOnlyOneStore || isLoading} // ADDED: Disable if only one store
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Store"
                        required
                        helperText={
                          hasOnlyOneStore
                            ? "Only one store assigned to your account"
                            : storeStatusState.isAdmin
                            ? "As admin, you can manage all stores"
                            : "You can only manage your assigned stores"
                        }
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <div className="flex flex-col">
                          <Typography variant="body2" className="font-medium">
                            {option.store_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {option.address}
                          </Typography>
                        </div>
                      </Box>
                    )}
                  />
                </FormControl>

                {/* Date Selection */}
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Status Date"
                    value={statusDate}
                    onChange={(newValue) => newValue && setStatusDate(newValue)}
                    maxDate={new Date()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        required
                        helperText={`Selected: ${formatDateToUSFormat(
                          statusDate
                        )}`}
                      />
                    )}
                    disabled={isLoading}
                  />
                </LocalizationProvider>

                {/* Status Selection */}
                <FormControl component="fieldset">
                  <FormLabel component="legend" className="mb-2">
                    Store Status
                  </FormLabel>
                  <RadioGroup
                    value={status}
                    onChange={(e) =>
                      setStatus(e.target.value as "open" | "closed")
                    }
                    row
                  >
                    <FormControlLabel
                      value="open"
                      control={<Radio color="success" />}
                      label={
                        <div className="flex items-center space-x-2">
                          <FaCheckCircle className="text-green-500" />
                          <span>Open</span>
                        </div>
                      }
                      disabled={isLoading}
                    />
                    <FormControlLabel
                      value="closed"
                      control={<Radio color="error" />}
                      label={
                        <div className="flex items-center space-x-2">
                          <FaTimesCircle className="text-red-500" />
                          <span>Closed</span>
                        </div>
                      }
                      disabled={isLoading}
                    />
                  </RadioGroup>
                </FormControl>

                {/* Notes */}
                <TextField
                  label="Notes (Optional)"
                  multiline
                  rows={3}
                  fullWidth
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any additional notes about the store status..."
                  disabled={isLoading}
                  helperText={`${notes.length}/255 characters`}
                  inputProps={{ maxLength: 255 }}
                />

                {/* Submit Button */}
                <Box className="flex justify-end">
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={!selectedStore || isLoading || isSubmitting}
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <FaSave />
                      )
                    }
                    sx={{
                      backgroundColor:
                        status === "open" ? "#22c55e" : "#ef4444",
                      "&:hover": {
                        backgroundColor:
                          status === "open" ? "#16a34a" : "#dc2626",
                      },
                    }}
                  >
                    {isSubmitting ? "Saving..." : "Save Status"}
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" className="mb-4 font-semibold">
                Quick Info
              </Typography>

              <div className="space-y-4">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Typography
                    variant="body2"
                    className="font-medium text-blue-800"
                  >
                    Daily Status Update
                  </Typography>
                  <Typography variant="caption" className="text-blue-600">
                    Update your store's open/closed status for today or previous
                    dates
                  </Typography>
                </div>

                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <Typography
                    variant="body2"
                    className="font-medium text-yellow-800"
                  >
                    Permission Level
                  </Typography>
                  <Typography variant="caption" className="text-yellow-600">
                    {storeStatusState.isAdmin
                      ? "Admin: Can manage all stores"
                      : "Store Manager: Can manage assigned stores only"}
                  </Typography>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <Typography
                    variant="body2"
                    className="font-medium text-gray-800"
                  >
                    Available Stores
                  </Typography>
                  <Typography variant="caption" className="text-gray-600">
                    You have access to {storeStatusState.userStores.length}{" "}
                    store(s)
                    {hasOnlyOneStore && " (Auto-selected)"}
                  </Typography>
                </div>

                {selectedStore && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <Typography
                      variant="body2"
                      className="font-medium text-green-800 mb-2"
                    >
                      {hasOnlyOneStore ? "Your Store" : "Selected Store"}
                      {hasOnlyOneStore && (
                        <Chip
                          label="Auto-selected"
                          size="small"
                          color="success"
                          className="ml-2"
                        />
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      className="text-green-600 block"
                    >
                      {selectedStore.store_name}
                    </Typography>
                    <Typography variant="caption" className="text-green-600">
                      {selectedStore.address}
                    </Typography>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
}
